import { BellIcon, BellOffIcon, BookXIcon, ClockIcon, FlameIcon, ListTodoIcon } from "lucide-react";
import { type ReactNode, useCallback } from "react";

import type { NotificationSettings, WrongAnswerTiming } from "@/api/notificationSettings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  useNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} from "@/hooks/queries/useNotificationSettingsQuery";
import { useNotificationPermission } from "@/hooks/useNotificationPermission";
import { trackEvent } from "@/lib/analytics";

const REMINDER_TIME_OPTIONS = [
  { value: "off", label: "끄기" },
  { value: "05:00", label: "오전 5시" },
  { value: "06:00", label: "오전 6시" },
  { value: "07:00", label: "오전 7시" },
  { value: "08:00", label: "오전 8시" },
  { value: "09:00", label: "오전 9시" },
  { value: "10:00", label: "오전 10시" },
  { value: "11:00", label: "오전 11시" },
  { value: "12:00", label: "오후 12시" },
  { value: "13:00", label: "오후 1시" },
  { value: "14:00", label: "오후 2시" },
  { value: "15:00", label: "오후 3시" },
  { value: "16:00", label: "오후 4시" },
  { value: "17:00", label: "오후 5시" },
  { value: "18:00", label: "오후 6시" },
  { value: "19:00", label: "오후 7시" },
  { value: "20:00", label: "오후 8시" },
  { value: "21:00", label: "오후 9시" },
  { value: "22:00", label: "오후 10시" },
] as const;

const WRONG_ANSWER_TIMING_OPTIONS = [
  { value: "off", label: "끄기" },
  { value: "next_morning", label: "다음날 아침" },
  { value: "after_hours", label: "시험 후 2시간" },
] as const;

function SwitchSkeleton() {
  return <Skeleton className="h-5 w-9 rounded-full" />;
}

function SettingRow({
  icon,
  title,
  description,
  disabled,
  control,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
  control: ReactNode;
}) {
  return (
    <div className={`flex items-center justify-between p-4 ${disabled ? "opacity-40" : ""}`}>
      <div className="flex items-center gap-4">
        {icon}
        <div>
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      {control}
    </div>
  );
}

export function NotificationSettingsSection() {
  const { data: settings, isPending, isError } = useNotificationSettingsQuery();
  const { mutate: update } = useUpdateNotificationSettingsMutation();
  const { isDenied: permissionDenied, isNativeApp } = useNotificationPermission();

  const handleToggle = useCallback(
    (key: keyof NotificationSettings, checked: boolean) => {
      trackEvent("notification_settings_changed", { setting: key, enabled: checked });
      update({ [key]: checked });
    },
    [update],
  );

  // DB 테이블 미적용 등으로 쿼리 실패 시 렌더링하지 않음
  if (isError) {
    return null;
  }

  const isDisabled = !isPending && !settings?.enabled;
  const dailyReminderValue = settings?.daily_reminder ? settings.daily_reminder_time : "off";
  const wrongAnswerValue = settings?.wrong_answer_review ? settings.wrong_answer_timing : "off";
  const dailyReminderLabel = REMINDER_TIME_OPTIONS.find(opt => opt.value === dailyReminderValue)?.label ?? "끄기";
  const wrongAnswerLabel = WRONG_ANSWER_TIMING_OPTIONS.find(opt => opt.value === wrongAnswerValue)?.label ?? "끄기";

  return (
    <section>
      <div className="rounded-xl bg-card overflow-hidden">
        {/* 마스터 토글 */}
        <SettingRow
          icon={
            isDisabled ? (
              <BellOffIcon className="size-4 text-muted-foreground" />
            ) : (
              <BellIcon className="size-4 text-muted-foreground" />
            )
          }
          title="알림"
          description="모든 알림 켜기/끄기"
          control={
            isPending ? (
              <SwitchSkeleton />
            ) : (
              <Switch checked={settings!.enabled} onCheckedChange={checked => handleToggle("enabled", checked)} />
            )
          }
        />

        {/* 기기 알림 권한 거부 안내 (네이티브 앱에서만 표시) */}
        {isNativeApp && permissionDenied && !isDisabled && (
          <div className="mx-4 mb-2 rounded-lg bg-destructive/10 px-3 py-2">
            <p className="text-xs text-destructive">
              기기의 알림 권한이 꺼져 있어요. 설정 앱에서 tobo 알림을 허용해 주세요.
            </p>
          </div>
        )}

        {/* 학습 리마인더 */}
        <SettingRow
          icon={<ClockIcon className="size-4 text-muted-foreground" />}
          title="학습 리마인더"
          description="매일 학습 시간 알림"
          disabled={isDisabled}
          control={
            isPending ? (
              <Skeleton className="h-8 w-24 rounded-md" />
            ) : (
              <Select
                value={dailyReminderValue}
                onValueChange={value => {
                  if (!value || value === "off") {
                    update({ daily_reminder: false });
                  } else {
                    update({ daily_reminder: true, daily_reminder_time: value });
                  }
                }}
                disabled={isDisabled}
              >
                <SelectTrigger size="sm">
                  <SelectValue>{dailyReminderLabel}</SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {REMINDER_TIME_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          }
        />

        {/* 미완료 테스트 알림 */}
        <SettingRow
          icon={<ListTodoIcon className="size-4 text-muted-foreground" />}
          title="미완료 테스트"
          description="진행 중인 테스트 리마인더"
          disabled={isDisabled}
          control={
            isPending ? (
              <SwitchSkeleton />
            ) : (
              <Switch
                checked={settings!.incomplete_test}
                onCheckedChange={checked => handleToggle("incomplete_test", checked)}
                disabled={isDisabled}
              />
            )
          }
        />

        {/* 오답 복습 리마인더 */}
        <SettingRow
          icon={<BookXIcon className="size-4 text-muted-foreground" />}
          title="오답 복습"
          description="틀린 문제 복습 알림"
          disabled={isDisabled}
          control={
            isPending ? (
              <Skeleton className="h-8 w-24 rounded-md" />
            ) : (
              <Select
                value={wrongAnswerValue}
                onValueChange={value => {
                  if (!value || value === "off") {
                    update({ wrong_answer_review: false });
                  } else {
                    update({ wrong_answer_review: true, wrong_answer_timing: value as WrongAnswerTiming });
                  }
                }}
                disabled={isDisabled}
              >
                <SelectTrigger size="sm">
                  <SelectValue>{wrongAnswerLabel}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {WRONG_ANSWER_TIMING_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          }
        />

        {/* 스트릭 알림 */}
        <SettingRow
          icon={<FlameIcon className="size-4 text-muted-foreground" />}
          title="연속 학습 알림"
          description="매일 학습 연속 기록 리마인더"
          disabled={isDisabled}
          control={
            isPending ? (
              <SwitchSkeleton />
            ) : (
              <Switch
                checked={settings!.streak_reminder}
                onCheckedChange={checked => handleToggle("streak_reminder", checked)}
                disabled={isDisabled}
              />
            )
          }
        />
      </div>
    </section>
  );
}
